'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

import { MegaphoneIcon, ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/local-api/client';
import Link from 'next/link';

interface AdPlacement {
  id: string;
  project_name: string;
  project_url: string | null;
  headline: string;
  description: string | null;
  cta_text: string | null;
  project_category: string;
  image_url: string | null;
  ad_type: string;
  display_seconds: number | null;
  payment_model?: string;
  end_date?: string | null;
}

interface AdSliderProps {
  placementSlot: string;
  variant?: 'banner' | 'compact';
  className?: string;
}

const DEFAULT_INTERVAL = 30;

// Sample placeholder data for banner variant
const SAMPLE_BANNER_ADS = [
  {
    id: 'sample-banner-1',
    headline: '🚀 Your Project Ad Here',
    description: 'Reach thousands of crypto investors & traders daily. Premium banner placement.',
    project_name: 'Your Brand',
    project_category: 'Crypto / DeFi / Forex',
    cta_text: 'Place Your Ad',
    project_url: '/place-ad',
    image_url: null,
    ad_type: 'banner',
    display_seconds: null,
    isSample: true,
    gradient: 'from-primary/20 via-primary/10 to-amber-500/15',
    accentColor: 'text-primary',
    badgeColor: 'bg-primary/15 text-primary',
  },
  {
    id: 'sample-banner-2',
    headline: '📈 Advertise Your Trading Platform',
    description: 'High-visibility banner slot. Thousands of active investors see this every day.',
    project_name: 'Your Platform',
    project_category: 'Investment / Trading',
    cta_text: 'Get Started',
    project_url: '/place-ad',
    image_url: null,
    ad_type: 'banner',
    display_seconds: null,
    isSample: true,
    gradient: 'from-violet-500/20 via-card to-blue-500/15',
    accentColor: 'text-violet-400',
    badgeColor: 'bg-violet-500/15 text-violet-400',
  },
  {
    id: 'sample-banner-3',
    headline: '💎 Premium Ad Slot Available',
    description: 'This banner placement gets maximum eyeballs. Perfect for DeFi, NFT & Web3 projects.',
    project_name: 'Your Project',
    project_category: 'DeFi / NFT / Web3',
    cta_text: 'Advertise Now',
    project_url: '/place-ad',
    image_url: null,
    ad_type: 'banner',
    display_seconds: null,
    isSample: true,
    gradient: 'from-amber-500/20 via-card to-orange-500/15',
    accentColor: 'text-amber-500',
    badgeColor: 'bg-amber-500/15 text-amber-500',
  },
];

// Sample placeholder data for compact variant
const SAMPLE_COMPACT_ADS = [
  {
    id: 'sample-compact-1',
    headline: 'Your Ad Here — Compact Slot',
    description: 'Inline sponsored placement. Seen by active investors browsing projects.',
    project_name: 'Your Brand',
    project_category: 'Sponsored',
    cta_text: 'Place Ad',
    project_url: '/place-ad',
    image_url: null,
    ad_type: 'compact',
    display_seconds: null,
    isSample: true,
  },
  {
    id: 'sample-compact-2',
    headline: 'Promote Your Project in This Slot',
    description: 'Compact inline ad — great for brand awareness among crypto traders.',
    project_name: 'Your Project',
    project_category: 'Crypto / Forex',
    cta_text: 'Advertise',
    project_url: '/place-ad',
    image_url: null,
    ad_type: 'compact',
    display_seconds: null,
    isSample: true,
  },
];

export default function AdSlider({ placementSlot, variant = 'banner', className = '' }: AdSliderProps) {
  const [ads, setAds] = useState<AdPlacement[]>([]);
  const [sampleAds] = useState(() =>
    variant === 'banner' ? SAMPLE_BANNER_ADS : SAMPLE_COMPACT_ADS
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [globalIntervalSeconds, setGlobalIntervalSeconds] = useState(DEFAULT_INTERVAL);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayAds = ads.length > 0 ? ads : (sampleAds as unknown as AdPlacement[]);
  const isSampleMode = ads.length === 0;

  const goToNext = useCallback(() => {
    if (displayAds.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % displayAds.length);
      setIsTransitioning(false);
    }, 300);
  }, [displayAds.length]);

  const goToPrev = useCallback(() => {
    if (displayAds.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + displayAds.length) % displayAds.length);
      setIsTransitioning(false);
    }, 300);
  }, [displayAds.length]);

  const goToIndex = useCallback((index: number) => {
    if (index === currentIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 300);
  }, [currentIndex]);

  const currentAdDuration = displayAds[currentIndex]?.display_seconds ?? globalIntervalSeconds;

  useEffect(() => {
    if (displayAds.length <= 1) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      goToNext();
    }, (isSampleMode ? 5 : currentAdDuration) * 1000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex, displayAds.length, currentAdDuration, goToNext, isSampleMode]);

  useEffect(() => {
    const supabase = createClient();

    supabase
      .from('ad_placements')
      .select('id, project_name, project_url, headline, description, cta_text, project_category, image_url, ad_type, display_seconds, payment_model, end_date')
      .eq('ad_status', 'approved')
      .eq('placement_slot', placementSlot === 'community_feed_inline' ? 'community_feed' : placementSlot)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setAds(data as AdPlacement[]);
        }
        setLoading(false);
      });

    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'ad_interval_seconds')
      .single()
      .then(({ data }) => {
        if (data?.value) {
          const parsed = parseInt(data.value, 10);
          if (!isNaN(parsed) && parsed > 0) {
            setGlobalIntervalSeconds(parsed);
          }
        }
      });
  }, [placementSlot]);

  useEffect(() => {
    const expiryTimes = ads.filter((ad) => ad.payment_model === 'free' && ad.end_date)
      .map((ad) => new Date(ad.end_date!).getTime()).filter(Number.isFinite);
    if (!expiryTimes.length) return;
    const timer = setTimeout(() => {
      setAds((previous) => previous.filter((ad) => ad.payment_model !== 'free'
        || (ad.end_date && new Date(ad.end_date).getTime() > Date.now())));
      setCurrentIndex(0);
    }, Math.min(2147483647, Math.max(0, Math.min(...expiryTimes) - Date.now())));
    return () => clearTimeout(timer);
  }, [ads]);

  if (loading) return null;

  const ad = displayAds[currentIndex] as AdPlacement & { isSample?: boolean; gradient?: string; accentColor?: string; badgeColor?: string };

  if (!isSampleMode && ad?.image_url && ['community_feed', 'community_feed_inline'].includes(placementSlot)) {
    return (
      <div className={`relative overflow-hidden rounded-2xl border border-border bg-card ${className}`}>
        <a href={ad.project_url || '#'} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${ad.project_name}`} className="block">
          <img src={ad.image_url} alt={ad.project_name} className="block h-auto w-full" />
        </a>
        <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/60 px-2 py-1 text-[10px] text-white">Sponsored</span>
        {displayAds.length > 1 && (
          <div className="absolute bottom-2 right-2 flex gap-2">
            <button onClick={goToPrev} aria-label="Previous ad" className="rounded-full bg-black/60 p-2 text-white"><ChevronLeftIcon className="h-4 w-4" /></button>
            <button onClick={goToNext} aria-label="Next ad" className="rounded-full bg-black/60 p-2 text-white"><ChevronRightIcon className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`relative min-h-[128px] bg-gradient-to-r from-amber-500/10 via-card to-primary/10 border ${isSampleMode ? 'border-dashed border-amber-500/40' : 'border-amber-500/20'} rounded-2xl overflow-hidden ${className}`}>
        <span className={`absolute top-2 right-2 z-20 text-[8px] font-bold uppercase tracking-[0.16em] px-2 py-1 rounded-full shadow-sm backdrop-blur ${isSampleMode ? 'text-white bg-amber-500' : 'text-muted-foreground bg-card/90 border border-border/60'}`}>
          {isSampleMode ? 'Sample' : 'Sponsored'}
        </span>
        {isSampleMode && (
          <span className="absolute bottom-2 left-2 z-20 text-[8px] text-white bg-black/55 px-1.5 py-0.5 rounded-full">Available slot</span>
        )}
        <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex min-h-[128px] sm:min-h-[144px]">
            {ad.image_url ? (
              <div className="relative w-[34%] min-w-[112px] max-w-[200px] flex-shrink-0 overflow-hidden bg-muted">
                <img src={ad.image_url} alt={ad.project_name} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent to-card/80" />
              </div>
            ) : (
              <div className="w-[34%] min-w-[112px] max-w-[200px] flex-shrink-0 bg-amber-500/15 flex items-center justify-center">
                <MegaphoneIcon style={{ width: 34, height: 34 }} className="text-amber-500" />
              </div>
            )}
            <div className={`flex min-w-0 flex-1 flex-col justify-center px-4 py-4 ${displayAds.length > 1 ? 'pr-11' : ''}`}>
              <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.15em] text-amber-500 truncate">{ad.project_category}</p>
              <p className="text-lg sm:text-2xl font-extrabold text-foreground leading-tight line-clamp-1" title={ad.project_name}>{ad.project_name}</p>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-snug line-clamp-2">{ad.headline}</p>
              <div className="mt-3 flex items-center gap-2">
                <Link
                  href={isSampleMode ? `/place-ad?slot=${placementSlot === 'community_feed_inline' ? 'community_feed' : placementSlot}` : (ad.project_url || '#')}
                  className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-bold text-white shadow-sm transition-colors hover:bg-amber-600"
                >
                  {ad.cta_text || 'Place Your Ad'}
                  <ArrowTopRightOnSquareIcon style={{ width: 11, height: 11 }} />
                </Link>
              </div>
            </div>
            {displayAds.length > 1 && (
              <div className="absolute right-2 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-1">
                <button onClick={goToPrev} className="p-1.5 rounded-full bg-card/85 shadow-sm backdrop-blur hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronLeftIcon style={{ width: 12, height: 12 }} />
                </button>
                <button onClick={goToNext} className="p-1.5 rounded-full bg-card/85 shadow-sm backdrop-blur hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronRightIcon style={{ width: 12, height: 12 }} />
                </button>
              </div>
            )}
          </div>
        </div>
        {displayAds.length > 1 && (
          <div className="absolute bottom-2 right-2 z-20 flex gap-1 rounded-full bg-card/80 px-1.5 py-1 backdrop-blur">
            {displayAds.map((_, i) => (
              <button key={i} onClick={() => goToIndex(i)} className={`rounded-full transition-all ${i === currentIndex ? 'w-4 h-1.5 bg-amber-500' : 'w-1.5 h-1.5 bg-muted-foreground/30'}`} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Banner variant
  const sampleGradient = ad.gradient || 'from-primary/20 via-primary/10 to-amber-500/15';
  const sampleAccent = ad.accentColor || 'text-primary';
  const sampleBadge = ad.badgeColor || 'bg-primary/15 text-primary';

  return (
    <div className={`relative bg-card border ${isSampleMode ? 'border-dashed border-primary/40' : 'border-primary/20'} rounded-xl overflow-hidden ${className}`}>
      {isSampleMode && (
        <div className="absolute top-0 right-0 z-20">
          <span className="text-[8px] font-bold uppercase tracking-widest text-white bg-primary px-2 py-0.5 rounded-bl-lg">Sample Slot</span>
        </div>
      )}
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {ad.image_url ? (
          <div className="relative w-full" style={{ minHeight: '160px' }}>
            <img src={ad.image_url} alt={ad.project_name} className="w-full object-cover" style={{ maxHeight: '220px', minHeight: '140px', width: '100%', display: 'block' }} />
            <div className="absolute top-2.5 left-3 flex items-center gap-1.5">
              <span className="text-[9px] font-bold uppercase tracking-widest text-white bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded-full">Sponsored</span>
              <span className="text-[9px] text-white/80 bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-full">{ad.project_category}</span>
            </div>
            {displayAds.length > 1 && (
              <>
                <button onClick={goToPrev} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors backdrop-blur-sm">
                  <ChevronLeftIcon style={{ width: 14, height: 14 }} />
                </button>
                <button onClick={goToNext} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors backdrop-blur-sm">
                  <ChevronRightIcon style={{ width: 14, height: 14 }} />
                </button>
              </>
            )}
          </div>
        ) : (
          /* Sample / no-image — rich placeholder */
          <div className={`relative w-full bg-gradient-to-br ${isSampleMode ? sampleGradient : 'from-primary/20 via-primary/10 to-amber-500/20'} flex flex-col items-center justify-center gap-3 py-8 px-6`} style={{ minHeight: '160px' }}>
            {/* Decorative circles */}
            <div className="absolute top-3 left-4 w-16 h-16 rounded-full bg-white/5 blur-xl" />
            <div className="absolute bottom-3 right-4 w-20 h-20 rounded-full bg-white/5 blur-xl" />
            <div className="absolute top-2.5 left-3 flex items-center gap-1.5 z-10">
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${isSampleMode ? sampleBadge : 'bg-muted text-muted-foreground'}`}>
                {isSampleMode ? 'Ad Slot' : 'Sponsored'}
              </span>
              <span className="text-[9px] text-muted-foreground">{ad.project_category}</span>
            </div>
            {displayAds.length > 1 && (
              <>
                <button onClick={goToPrev} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors z-10">
                  <ChevronLeftIcon style={{ width: 14, height: 14 }} />
                </button>
                <button onClick={goToNext} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors z-10">
                  <ChevronRightIcon style={{ width: 14, height: 14 }} />
                </button>
              </>
            )}
            <div className="relative z-10 flex flex-col items-center gap-2 text-center max-w-xs">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isSampleMode ? 'bg-white/10' : 'bg-primary/20'}`}>
                {isSampleMode ? (
                  <SparklesIcon style={{ width: 22, height: 22 }} className={sampleAccent} />
                ) : (
                  <MegaphoneIcon style={{ width: 22, height: 22 }} className="text-primary" />
                )}
              </div>
              <p className={`text-base font-bold leading-snug ${isSampleMode ? sampleAccent : 'text-foreground'}`}>{ad.headline}</p>
              {ad.description && (
                <p className="text-xs text-muted-foreground leading-relaxed">{ad.description}</p>
              )}
              <Link
                href={isSampleMode ? `/place-ad?slot=${placementSlot === 'community_feed_inline' ? 'community_feed' : placementSlot}` : (ad.project_url || '#')}
                className={`mt-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${isSampleMode ? `${sampleBadge} hover:opacity-80` : 'bg-primary/15 text-primary hover:bg-primary/25'}`}
              >
                {ad.cta_text || 'Place Your Ad'}
                <ArrowTopRightOnSquareIcon style={{ width: 11, height: 11 }} />
              </Link>
            </div>
          </div>
        )}

        {/* Bottom info bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/50">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {isSampleMode ? 'Available Slot' : 'Sponsored'}
            </span>
            <span className="text-xs font-semibold text-foreground truncate">{ad.headline}</span>
          </div>
          <Link
            href={isSampleMode ? `/place-ad?slot=${placementSlot === 'community_feed_inline' ? 'community_feed' : placementSlot}` : (ad.project_url || '#')}
            className="flex items-center gap-1 text-[11px] font-semibold text-amber-500 hover:underline flex-shrink-0"
          >
            {isSampleMode ? 'Book This Slot' : (ad.cta_text || 'Learn More')}
            <ArrowTopRightOnSquareIcon style={{ width: 10, height: 10 }} />
          </Link>
        </div>
      </div>

      {/* Dot indicators */}
      {displayAds.length > 1 && (
        <div className="flex justify-center gap-1 pb-2">
          {displayAds.map((_, i) => (
            <button key={i} onClick={() => goToIndex(i)} className={`rounded-full transition-all ${i === currentIndex ? 'w-4 h-1.5 bg-amber-500' : 'w-1.5 h-1.5 bg-muted-foreground/30'}`} />
          ))}
        </div>
      )}
    </div>
  );
}
